/*
 * 照片标签真实接口层
 * 对应接口文档 WebXCameraAPI - 照片标签
 */
import type { PhotoTag, PhotoTagGroup } from './types'

declare const __API_SERVER_HOST__: string

// ─── 服务端返回的原始类型 ───────────────────────────────────────

interface ApiLabel {
  id: string
  labelGroupID: string
  name: string
  color: string
  scope: string
  sort: number
  creatorUserID: string
  useCount: number
  disabled?: boolean
}

interface ApiLabelGroup {
  id: string
  name: string
  scope: string
  sort: number
  labelCount: number
  labels: ApiLabel[]
}

interface ApiManageData {
  status: number
  msg: string
  team: { scope: string; groups: ApiLabelGroup[] }
  custom: { scope: string; groups: ApiLabelGroup[] }
  tabStats: {
    teamLabelCount: number
    customLabelCount: number
    teamGroupCount: number
    customGroupCount: number
  }
}

// ─── 类型适配器 ────────────────────────────────────────────────

function toPhotoTag(label: ApiLabel): PhotoTag {
  return {
    id: String(label.id),
    name: label.name,
    scope: label.scope === 'custom' ? 'personal' : 'team',
    color: label.color,
    order: label.sort,
    useCount: label.useCount,
    disabled: Boolean(label.disabled)
  }
}

function toGroupWithTags(
  group: ApiLabelGroup
): PhotoTagGroup & { tags: PhotoTag[] } {
  const labels = group.labels || []
  return {
    id: String(group.id),
    name: group.name,
    scope: 'team',
    tagIds: labels.map((l) => String(l.id)),
    order: group.sort,
    tags: labels.map(toPhotoTag)
  }
}

// ─── 基础请求封装 ──────────────────────────────────────────────

async function post<T>(path: string, body: object): Promise<T> {
  const res = await fetch(__API_SERVER_HOST__ + path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body)
  })
  if (!res.ok) {
    throw new Error('网络请求失败，请稍后重试')
  }
  const result = await res.json()
  if (result.data?.status !== 0) {
    throw new Error(result.data?.msg || '操作失败')
  }
  return result.data as T
}

// ─── 1. 标签管理首页（拉取全量数据）────────────────────────────
// 请求去重：短时间内多次调用共享同一个 Promise，避免重复请求
const _pendingManage: Map<string, Promise<ApiManageData>> = new Map()

export async function fetchManageData(
  groupID: string,
  userID: string
): Promise<ApiManageData> {
  const cacheKey = `${groupID}-${userID}`
  const pending = _pendingManage.get(cacheKey)
  if (pending) return pending
  const p = post<ApiManageData>('/web/workgroup/photo/label/manage', {
    groupID,
    userID
  }).finally(() => {
    _pendingManage.delete(cacheKey)
  })
  _pendingManage.set(cacheKey, p)
  return p
}

// ─── 衍生查询：团队标签（扁平列表，供计数用）──────────────────

export async function fetchTeamTags(
  groupID: string,
  userID: string
): Promise<PhotoTag[]> {
  const data = await fetchManageData(groupID, userID)
  const groups = data.team?.groups || []
  return groups.flatMap((g) => (g.labels || []).map(toPhotoTag))
}

// ─── 衍生查询：个人标签 + 默认分组 ID ─────────────────────────

export async function fetchPersonalTags(
  groupID: string,
  userID: string
): Promise<{ tags: PhotoTag[]; defaultGroupId: string | null }> {
  const data = await fetchManageData(groupID, userID)
  const customGroups = data.custom?.groups || []
  const defaultGroup = customGroups[0] ?? null
  const tags = customGroups.flatMap((g) => (g.labels || []).map(toPhotoTag))
  return { tags, defaultGroupId: defaultGroup?.id ?? null }
}

// ─── 衍生查询：团队标签组（含组内标签，供 TeamTagSection 用）──

export async function fetchTagGroups(
  groupID: string,
  userID: string
): Promise<Array<PhotoTagGroup & { tags: PhotoTag[] }>> {
  const data = await fetchManageData(groupID, userID)
  const groups = data.team?.groups || []
  return groups.map(toGroupWithTags)
}

// ─── 2/3. 新建 / 更新标签组 ────────────────────────────────────

export async function createOrUpdateTagGroup(
  groupID: string,
  userID: string,
  payload: { id?: string; name: string }
): Promise<PhotoTagGroup & { tags: PhotoTag[] }> {
  if (payload.id) {
    // 更新
    const data = await post<{
      group: { id: string; name: string; scope: string; sort: number }
    }>('/web/workgroup/photo/label/group/update', {
      groupID,
      labelGroupID: payload.id,
      name: payload.name
    })
    return {
      id: String(data.group.id),
      name: data.group.name,
      scope: 'team',
      tagIds: [],
      order: data.group.sort,
      tags: []
    }
  }
  // 新建
  const data = await post<{ group: ApiLabelGroup }>(
    '/web/workgroup/photo/label/group/create',
    { groupID, userID, scope: 'team', name: payload.name }
  )
  return toGroupWithTags(data.group)
}

// ─── 4/5. 新建 / 更新团队标签 ──────────────────────────────────

export async function createOrUpdateTeamTag(
  groupID: string,
  payload: {
    id?: string
    name: string
    color?: string
    groupId?: string
    // 仅 update 时有效。true=停用,false=启用,undefined=不改。
    // 停用后该标签不在 manage/batch (打标场景) 下发, TagPickerPopover 看不到。
    disabled?: boolean
  }
): Promise<PhotoTag> {
  if (payload.id) {
    // 更新
    const body: Record<string, unknown> = {
      groupID,
      labelID: payload.id,
      name: payload.name,
      color: payload.color ?? '#2979FF'
    }
    if (typeof payload.disabled === 'boolean') {
      body.disabled = payload.disabled
    }
    const data = await post<{ label: ApiLabel }>(
      '/web/workgroup/photo/label/update',
      body
    )
    return toPhotoTag(data.label)
  }
  // 新建
  const data = await post<{ label: ApiLabel }>(
    '/web/workgroup/photo/label/create',
    {
      groupID,
      labelGroupID: payload.groupId,
      name: payload.name,
      color: payload.color ?? '#2979FF'
    }
  )
  return toPhotoTag(data.label)
}

// ─── 4/5. 新建 / 更新个人标签（custom scope）──────────────────

export async function createOrUpdatePersonalTag(
  groupID: string,
  payload: { id?: string; name: string; color?: string; customGroupId?: string }
): Promise<PhotoTag> {
  if (payload.id) {
    // 更新
    const data = await post<{ label: ApiLabel }>(
      '/web/workgroup/photo/label/update',
      {
        groupID,
        labelID: payload.id,
        name: payload.name,
        color: payload.color ?? '#2979FF'
      }
    )
    return toPhotoTag(data.label)
  }
  // 新建
  if (!payload.customGroupId) {
    throw new Error('未找到个人标签分组，请刷新页面后重试')
  }
  const data = await post<{ label: ApiLabel }>(
    '/web/workgroup/photo/label/create',
    {
      groupID,
      labelGroupID: payload.customGroupId,
      name: payload.name,
      color: payload.color ?? '#2979FF'
    }
  )
  return toPhotoTag(data.label)
}

// ─── 5b. 删除标签 (软删) ──────────────────────────────────────
// 业务规则:
//   - useCount = 0 → 后端允许硬删 (软删 deleted_at)
//   - useCount > 0 → 后端拒绝, 调用方应改用 createOrUpdateTeamTag({ id, disabled: true }) 走"停用"
//
// 调用此函数前 UI 层应保证 useCount = 0;若仍传给后端被拒,异常会从 post() 抛出 (msg=已被使用)。
export async function deleteLabel(
  groupID: string,
  labelID: string
): Promise<void> {
  await post<{ status: number; msg: string }>(
    '/web/workgroup/photo/label/delete',
    { groupID, labelID }
  )
}

// ─── 6. 下发配置 ──────────────────────────────────────────────

export interface PhotoLabelConfigPayload {
  forceLabel: boolean
  remindWhenNotForce: boolean
  remindScenes: string[]
}

export interface PhotoLabelGroupConfigItem {
  groupID: string
  config: PhotoLabelConfigPayload
}

const DEFAULT_CONFIG: PhotoLabelConfigPayload = {
  forceLabel: false,
  remindWhenNotForce: true,
  remindScenes: ['preview', 'watermark', 'menu']
}

export async function getPhotoLabelConfig(
  groupID: string
): Promise<PhotoLabelConfigPayload> {
  const data = await post<{ list: PhotoLabelGroupConfigItem[] }>(
    '/web/workgroup/photo/label/config/get',
    {}
  )
  const item = (data.list || []).find(
    (i) => String(i.groupID) === String(groupID)
  )
  return item?.config ?? { ...DEFAULT_CONFIG }
}

export async function setPhotoLabelConfig(
  groupID: string,
  config: PhotoLabelConfigPayload
): Promise<PhotoLabelConfigPayload> {
  const data = await post<{ item: PhotoLabelGroupConfigItem }>(
    '/web/workgroup/photo/label/config/set',
    { groupID, config }
  )
  return data.item?.config ?? config
}

// ─── 7. 批量给照片打标签 ──────────────────────────────────────

interface BatchBindResult {
  status: number
  msg: string
  boundPhotoCount: number
  boundLabelCount: number
}

export async function batchBindLabels(params: {
  groupID: string
  filter: any
  labelIDs: string[]
  isPreciseSearch?: boolean
  /** exclusive=true 时服务端先清空命中 photos 的所有 rel 再 bind 新标签.
   *  用于「一张照片只能 1 个标签」的产品约束 (单照片切换 / 批量打标覆盖).
   *  默认 false = append 旧行为. */
  exclusive?: boolean
}): Promise<BatchBindResult> {
  return post<BatchBindResult>('/web/workgroup/photo/label/bind', params)
}

interface BatchUnbindResult {
  status: number
  msg: string
  removedRelCount: number
  affectedPhotoCount: number
  affectedLabelCount: number
}

/**
 * 批量从照片移除标签。
 * - 单照片单标签:filter.include=[photo.file], labelIDs=[lid]
 * - 批量 N 张 × M 标签:filter=当前筛选条件 + checkAll/include/exclude, labelIDs=[...]
 * - 替换 A→B:外层调用方先 batchUnbindLabels(A) 再 batchBindLabels(B), 两步走
 * 后端 ES 同步走 syncPhotoLabelsToSearch 跟 bind 共用同一函数,删完 photo doc 的 labels 字段会自动收敛到新集合。
 */
export async function batchUnbindLabels(params: {
  groupID: string
  filter: any
  labelIDs: string[]
  isPreciseSearch?: boolean
}): Promise<BatchUnbindResult> {
  return post<BatchUnbindResult>('/web/workgroup/photo/label/unbind', params)
}

// ─── 8. 团队打标合规明细（按成员，近 N 天） ─────────────────────

export interface PhotoLabelStatsTotal {
  tagged: number
  untagged: number
  photos: number
}

export interface PhotoLabelStatsMember {
  userID: string
  name: string
  tagged: number
  untagged: number
  photos: number
}

interface PhotoLabelStatsResp {
  status: number
  msg: string
  total: PhotoLabelStatsTotal
  members: PhotoLabelStatsMember[]
}

export async function fetchPhotoLabelStatsByMember(
  groupID: string,
  days = 7
): Promise<{ total: PhotoLabelStatsTotal; members: PhotoLabelStatsMember[] }> {
  const data = await post<PhotoLabelStatsResp>(
    '/web/workgroup/photo/label/stats/byMember',
    { groupID, days }
  )
  return {
    total: data.total ?? { tagged: 0, untagged: 0, photos: 0 },
    members: data.members ?? []
  }
}

// ─── 9. 照片标签灰度判断（后端接口，替换旧 KV 本地比对）───────────
// module-level 缓存 + in-flight 去重。
// usePhotoLabelEnabled 被 8+ 处使用（照片 chip 按张渲染，同一页面可能触发上百次），
// 加缓存后：60s TTL 内同一 groupID 直接命中无网络；缓存未命中但已有请求在途时复用同一 Promise。
// TTL 60s 与后端 dao.photoLabelWhitelistCacheTTL 对齐。
const PHOTO_LABEL_ENABLED_TTL_MS = 60 * 1000
const _photoLabelEnabledCache = new Map<
  string,
  { value: boolean; expiresAt: number }
>()
const _pendingPhotoLabelEnabled = new Map<string, Promise<boolean>>()

interface PhotoLabelWhitelistCheckResp {
  status: number
  msg: string
  enabled: boolean
}

export async function fetchPhotoLabelEnabled(
  groupID: string
): Promise<boolean> {
  if (!groupID) return false

  const now = Date.now()
  const hit = _photoLabelEnabledCache.get(groupID)
  if (hit && hit.expiresAt > now) {
    return hit.value
  }

  const inflight = _pendingPhotoLabelEnabled.get(groupID)
  if (inflight) {
    return inflight
  }

  const request = post<PhotoLabelWhitelistCheckResp>(
    '/web/workgroup/photo/label/whitelist/check',
    { groupID }
  )
    .then((data) => Boolean(data.enabled))
    .catch(() => false)
    .then((value) => {
      _photoLabelEnabledCache.set(groupID, {
        value,
        expiresAt: Date.now() + PHOTO_LABEL_ENABLED_TTL_MS
      })
      return value
    })
    .finally(() => {
      _pendingPhotoLabelEnabled.delete(groupID)
    })

  _pendingPhotoLabelEnabled.set(groupID, request)
  return request
}

export function invalidatePhotoLabelEnabledCache(groupID?: string): void {
  if (groupID) {
    _photoLabelEnabledCache.delete(groupID)
    _pendingPhotoLabelEnabled.delete(groupID)
    return
  }
  _photoLabelEnabledCache.clear()
  _pendingPhotoLabelEnabled.clear()
}

// ─── 8. 批量拉多团队的「团队标签 + ForceLabel」──────────────────
// 单次最多 50 个 groupID（后端会截断）。后端只校验"用户在团队内"，不在的 groupID 静默丢掉。

export interface PhotoLabelBatchItem {
  groupID: string
  labels: PhotoTag[]
  forceLabel: boolean
}

interface PhotoLabelManageBatchRespItem {
  groupID: string
  labels: ApiLabel[]
  config: { forceLabel: boolean }
}

interface PhotoLabelManageBatchResp {
  status: number
  msg: string
  items: PhotoLabelManageBatchRespItem[]
}

export async function fetchPhotoLabelManageBatch(
  groupIDs: string[]
): Promise<PhotoLabelBatchItem[]> {
  if (!groupIDs.length) return []
  const data = await post<PhotoLabelManageBatchResp>(
    '/web/workgroup/photo/label/manage/batch',
    { groupIDs }
  )
  return (data.items ?? []).map((item) => ({
    groupID: item.groupID,
    labels: (item.labels ?? []).map(toPhotoTag),
    forceLabel: Boolean(item.config?.forceLabel)
  }))
}

// ─── 关闭整个照片标签功能（团队级一键关闭，仅管理员）──────────────
// 后端把该团队 photo_label_whitelist.enabled 置 0（+ disabled_by_admin 标记）并停用所有标签，
// PC / App 两端的白名单查询（enabled=1）自然排除该团队，功能两端一起隐藏。
// 不可逆，重新开启需运营改库（enabled=1, disabled_by_admin=0）。

export async function disablePhotoLabelFeature(groupID: string): Promise<void> {
  await post<{ status: number; msg: string }>(
    '/web/workgroup/photo/label/feature/disable',
    { groupID }
  )
}
